/** Canvas长图 */

import logo from '@assets/imgs/logo-color.svg';
import Loading from '@component/Loading/Loading';
import type { IFrameInstanceType } from '@component/ShareActiveModal/CanvasPosterDialog/IframeCanvas';
import { usePx2RemRate } from '@hooks/usePx2RemRate';
import { getCanvasData } from '@model/register-canvas';
import ActivitiesPublicRegistrationPage from '@quickService/ActivitiesPublicRegistrationPage';
import PublicRegistrationQueryEventSchedulers from '@quickService/PublicRegistrationQueryEventSchedulers';
import { Bubble, Button, StatusTip, message } from '@tencent/tea-component';
import GenerateQrcode from '@tencent/tpm-meeting-active/lib/GenerateQrcode';
import { downloadBlob } from '@utils/download-file';
import dynamic from 'next/dynamic';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { createSimpleFromEvent } from 'src/pages/event/[eventid]/registration/buyer/controller/genCanvasData';
import useSWR from 'swr';
import { SecondaryDialog, SecondaryDialogProps, SecondDialogProps } from '../CopyLinkDialog';
import './index.scss';
import { uploadFile } from './uploadBlob';

const IframeCanvas = dynamic(
  () => import('@component/ShareActiveModal/CanvasPosterDialog/IframeCanvas'),
);

const useCanvasData = (eventId: string | undefined) =>
  useSWR(
    () => (eventId ? `/canvasData/${eventId}` : false),
    () =>
      getCanvasData(eventId!, false).then((data) => {
        const { schema, packages, isCanvas } = data;
        if (isCanvas && !schema) {
          return Promise.all([
            ActivitiesPublicRegistrationPage({ event_id: eventId }),
            PublicRegistrationQueryEventSchedulers({
              event_id: eventId,
              guest_image_type: 'mid',
              scheduler_pic_image_type: 'mid',
            }),
          ]).then(([event, schedules]) =>
            createSimpleFromEvent(event.data, schedules.data.scheduler_data_list).then((cData) => ({
              packages: cData.packages,
              schema: schemaFilter(cData.schema),
            })),
          );
        }
        return schema ? { schema: schemaFilter(schema), packages } : {};
      }),
    { revalidateOnFocus: false },
  );

// 在列表页面有多个图片同时在生成, 需要隔离状态
type CacheData = { cosId: string; generating: boolean; uploading: boolean; cacheUrl: string };
const stateCache: Record<string, CacheData> = {};
const useGenerateState = (eventId?: string) => {
  const cacheData = useRef<CacheData>({} as unknown as CacheData);
  const [cosId, setCosId] = useState<string>();
  useEffect(() => {
    if (!eventId) return;
    cacheData.current = stateCache[eventId!] || {};
    setCosId(cacheData.current.cosId);
    return () => {
      stateCache[eventId] = cacheData.current || {};
    };
  }, [eventId]);
  const updateCosId = useCallback(
    (id: string) => {
      setCosId(id);
      cacheData.current.cosId = id;
    },
    [eventId],
  );
  return {
    cosId,
    setCosId: updateCosId,
    processData: cacheData,
  };
};

export const CanvasPosterDialog = memo<SecondDialogProps>(({ event, visible, onBack, onClose }) => {
  const eventId = event?.event_id;
  const [loading, setLoading] = useState(true);
  const iframeWrap = useRef<HTMLDivElement>(null);
  const { data: canvasData, mutate } = useCanvasData(eventId);
  const iframeRenderRef = useRef<IFrameInstanceType>();
  const rate = usePx2RemRate();
  const observeMute = useRef<ResizeObserver>();
  const { cosId, setCosId, processData } = useGenerateState(eventId);

  const generateImage = useCallback(() => {
    if (processData.current.cacheUrl) return Promise.resolve(processData.current.cacheUrl);
    if (processData.current.generating) return;
    if (!iframeRenderRef.current?.takeScreenshot) {
      message.error({ content: '页面未就绪,请稍后重试' });
      return;
    }
    processData.current.generating = true;
    // 先刷新一次内容,防止图片过期
    return mutate().then(
      () =>
        iframeRenderRef.current
          ?.takeScreenshot()
          .then((img) => {
            processData.current.cacheUrl = img;
            return img;
          })
          .finally(() => {
            processData.current.generating = false;
          }),
    );
  }, []);

  const onShareMobile = useCallback(
    (visible: boolean) => {
      if (!visible) return;
      generateImage()?.then((url) => {
        if (cosId) return;
        if (processData.current.uploading) return;
        processData.current.uploading = true;
        uploadFile(url!, eventId!)
          .then((data) => {
            setCosId(data.cosId);
          })
          .finally(() => {
            processData.current.uploading = false;
          });
      });
    },
    [generateImage, eventId, cosId],
  );

  const buttons = useMemo(
    () =>
      [
        {
          txt: '下载到电脑',
          onClick() {
            const q = generateImage();
            if (!q) return;
            const loading = message.loading({ content: '正在生成图片', duration: 0 });
            // wait(20) 是因为有缓存的情况下立即 finally 关闭 loading 会导致 loading 取消不掉
            Promise.all([q, wait(20)])
              .then(([img]) => {
                if (!img) return;
                downloadBlob(img, event?.title || '分享图片');
              })
              .finally(() => {
                loading.hide();
              });
          },
        },
        <Bubble
          onVisibleChange={onShareMobile}
          placement="top"
          content={
            <>
              <div className="share-bubble-qrcode-tips">扫描下方二维码下载长图至手机</div>
              <div className="share-bubble-qrcode">
                {!cosId ? (
                  <StatusTip status="loading" loadingText="图片生成中..." />
                ) : (
                  <GenerateQrcode
                    link={`${location.protocol}//${location.host}/events/host/canvas-poster?cos=${cosId}`}
                    size={168}
                    imageSettings={{
                      src: logo,
                      width: 20,
                      height: 20,
                      excavate: true,
                    }}
                  />
                )}
              </div>
            </>
          }
        >
          <Button type="primary">用手机分享</Button>
        </Bubble>,
      ] as SecondaryDialogProps['buttons'],
    [eventId, cosId],
  );

  useLayoutEffect(() => {
    window.React = React;
    window.ReactDOM = ReactDOM;
    return () => {
      observeMute.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    !visible && observeMute.current?.disconnect();
  }, [visible]);

  const onReady = useCallback(
    (ref: IFrameInstanceType) => {
      const { win, doc, ifr } = ref;
      iframeRenderRef.current = ref;
      if (!win || !doc) return;
      // eslint-disable-next-line no-param-reassign
      (win as any).event_id = eventId;
      const appEl = doc.querySelector('#app') as HTMLDivElement;
      const updateIframeHeight = () => {
        if (!iframeWrap.current) return;
        const bodyHeight = (appEl.offsetHeight || 0) + 5;
        // debounce
        if (bodyHeight - parseFloat(ifr?.style.height || '0') < 20) {
          return;
        }
        // eslint-disable-next-line no-param-reassign
        if (ifr) ifr.style.height = `${bodyHeight}px`;
        iframeWrap.current!.style.height = `${bodyHeight * rate}px`;
        bodyHeight > 100 && setLoading(false);
      };
      observeMute.current?.disconnect();
      observeMute.current = new ResizeObserver(updateIframeHeight);
      observeMute.current.observe(appEl);
      updateIframeHeight();
    },
    [eventId, rate, loading],
  );

  const onError = useCallback((msg: string) => {
    console.error(msg);
    window.nextAegis.infoAll('share-dialog-canvas-poster-error', msg);
  }, []);

  const onDlgClose = useCallback(() => {
    processData.current = {} as unknown as CacheData;
    onClose();
  }, [onClose]);

  return (
    <SecondaryDialog
      className="publish-share-canvas"
      title="下载长图分享活动"
      buttons={buttons}
      visible={visible}
      onClose={onDlgClose}
      onBack={onBack}
    >
      <div className="publish-share-canvas-content">
        <div className="poster-preview">
          <Loading show={loading} logo="meeting" />
          <div className="poster-preview-iframe-wrap" ref={iframeWrap}>
            <IframeCanvas
              onError={onError}
              onReady={onReady}
              canvasData={canvasData!}
              printMode
              style={{ '--scale': rate }}
            />
          </div>
        </div>
      </div>
    </SecondaryDialog>
  );
});
CanvasPosterDialog.displayName = 'CanvasPosterDialog';

/** 过滤掉不需要显示在长图上的组件，包括: 导航条 */
export const schemaFilter = (schema: any) => {
  if (!schema) return schema;
  return {
    ...schema,
    children: schema.children.filter((c: any) => !c.componentName.match(/Nav/)),
  };
};

function wait(t: number) {
  return new Promise<void>((res) => setTimeout(() => res(), t));
}
